import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { COMPANY, NAV_LINKS, SALES_PHONE, TECH_PHONE, WHATSAPP_LINK } from "@/data/site";

const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const Footer = () => (
  <footer className="bg-navy text-slate-300" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[1.3fr_1fr_1.2fr] lg:gap-16">
      <div>
        <div className="flex items-center gap-3">
          <img
            src="/ambrotech-emblem.png"
            alt="Ambrotech Private Limited logo"
            className="h-12 w-12 rounded-md bg-white object-contain p-1"
            width="48"
            height="48"
          />
          <span className="leading-none">
            <span className="block font-display text-xl font-bold tracking-[0.08em] text-white">AMBROTECH</span>
            <span className="mt-1 block text-[10px] font-medium tracking-[0.3em] text-slate-400">PRIVATE LIMITED</span>
          </span>
        </div>
        <p className="mt-5 font-display text-sm font-semibold text-brand">{COMPANY.tagline}</p>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
          Suppliers of solar energy products, electrical components, and related equipment for
          residential, commercial, and industrial requirements.
        </p>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">Quick Links</h3>
        <ul className="mt-5 space-y-3">
          {NAV_LINKS.map((l) => (
            <li key={l.path}>
              <Link
                to={l.path}
                className="text-sm text-slate-400 transition-colors hover:text-brand"
                data-testid={`footer-link-${l.path === "/" ? "home" : l.path.slice(1)}`}
              >
                {l.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">Contact</h3>
        <ul className="mt-5 space-y-4 text-sm">
          <li className="flex items-start gap-3">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span>
              <span className="block text-slate-400">Sales Enquiry (calls only)</span>
              <a href={SALES_PHONE.tel} className="font-semibold text-white transition-colors hover:text-brand" data-testid="footer-sales-phone">
                {SALES_PHONE.label}
              </a>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span>
              <span className="block text-slate-400">Technical Support</span>
              <a href={TECH_PHONE.tel} className="font-semibold text-white transition-colors hover:text-brand" data-testid="footer-tech-phone">
                {TECH_PHONE.label}
              </a>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <WhatsAppGlyph className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-white transition-colors hover:text-brand"
              data-testid="footer-whatsapp-link"
            >
              WhatsApp: {TECH_PHONE.label}
            </a>
          </li>
          <li className="flex items-start gap-3">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <a
              href={`mailto:${COMPANY.email}`}
              className="font-semibold text-white transition-colors hover:text-brand"
              data-testid="footer-email-link"
            >
              {COMPANY.email}
            </a>
          </li>
          <li className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span className="text-slate-400">
              Delhi · Patna · Silvassa
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 py-6 text-center text-xs text-slate-500 sm:px-8">
        © 2026 {COMPANY.name}. All rights reserved.
      </div>
    </div>
  </footer>
);

export { WhatsAppGlyph };
export default Footer;